/**
 * Сессия API: токены в localStorage, настройки интерфейса (§Т5.5).
 *
 * Назначение файла: кошелёк и мир больше не живут в браузере — только JWT и prefs.
 */

const SESSION_KEY = "hive.session.v1";

export type Session = {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly email: string;
};

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (
      typeof parsed.accessToken !== "string" ||
      typeof parsed.refreshToken !== "string" ||
      typeof parsed.email !== "string"
    ) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      email: parsed.email,
    };
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // приватный режим
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
  pending = null;
}

/** Ответ сервера на обновление пары токенов. */
export type RefreshedTokens = {
  readonly accessToken: string;
  readonly refreshToken: string;
};

/** Незавершённое обновление: одно на весь клиент, см. пояснение ниже. */
let pending: Promise<Session | null> | null = null;

/**
 * Обновить сессию не более одного раза, сколько бы запросов этого ни просили.
 *
 * Refresh-токен одноразовый: сервер отзывает его при обмене. Когда приложение
 * просыпается через час, запросы профиля, кошелька, Следов и двора уходят
 * пачкой, получают 401 одновременно и без этой воронки обменивают один и тот же
 * токен наперегонки: первый обмен удаётся, остальные получают
 * auth.refresh_invalid и выбрасывают игрока на экран входа. Именно так сессия
 * умирала на телефоне.
 */
export function refreshSessionOnce(
  exchange: (refreshToken: string) => Promise<RefreshedTokens>,
): Promise<Session | null> {
  if (pending) return pending;

  const started = (async (): Promise<Session | null> => {
    const current = loadSession();
    if (!current) return null;
    try {
      const tokens = await exchange(current.refreshToken);
      const next: Session = {
        ...current,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      };
      saveSession(next);
      return next;
    } catch {
      return null;
    }
  })();

  pending = started;
  void started.finally(() => {
    if (pending === started) pending = null;
  });
  return started;
}
